/**
 * The actor handlers (D232, actors round A) — what the engine DOES with a
 * body once narrowing has let a verb through to it.
 *
 * @remarks
 * Looking is one act an actor answers besides speech (which `handleAsk`
 * answers for every party); D240 adds the second, going THROUGH a body that
 * carries something. The other item verbs never get here: the actor
 * intercept refuses them before a handler is chosen, in the actor's words
 * or the `verbOnActor` slot's — an actor cannot be carried because it is not
 * an item, and nothing below has to check.
 * Round D's strike has its own module (`attack-handler.ts`); what this one
 * learned from D234 is that a fallen body reads differently.
 *
 * @packageDocumentation
 */

import type { NarrativeLine, RuntimeState } from '@orpheus/content-schema';

import type { RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireActor, requireItem } from '../bundle/bundle-lookups.js';
import { isActorDefeated } from '../entities/actor-combat.js';
import { entityRefForActor } from '../entities/entity-ref.js';
import { withMembership, withWorld } from '../entities/item-state.js';
import { describeItemInState, resolveConditionalText } from '../narrative/conditional-text.js';
import { type ActionHandlerResult, completed, completedSpeaking } from './action-handler-result.js';
import { nothingFoundLines, revealLinesOf } from './container-handlers.js';
import { stampNarrativeLines, type TranscriptCursor } from './transcript-cursor.js';

/**
 * `examine <actor>` — the player SEEING them (D232; `actors.md` §2: "an
 * actor's description is in the environment channel — it is the player
 * seeing them, not them speaking").
 *
 * @remarks
 * The one describer items read (`describeItemInState`: the description's
 * reading now, with its fragments appended while they hold) — so an actor
 * who looks different once the mask is taken is authored exactly as a
 * kettle that looks different once it is on. D234: a FALLEN body reads its
 * `defeatedDescription` instead, when its combat block authors one — the
 * body where it fell, in the words the pack chose for that — and otherwise
 * its ordinary page (a fragment on `actor_defeated` is the other way to say
 * it). Stamped under
 * `inspect:<actorId>`, the context every look carries, so a trigger can
 * listen for "the player has looked at her" as it listens for a thing.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param action - The narrowed look
 * @param cursor - This turn's transcript cursor
 * @returns The completed look — reach was judged at narrowing; a look is never refused here
 */
export const handleInspectActor = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'inspect_actor' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const actor = requireActor(bundle, action.actorId);
	const fallenReading = actor.combat?.defeatedDescription;
	const text =
		fallenReading !== undefined && isActorDefeated(bundle, state, action.actorId)
			? resolveConditionalText(fallenReading, bundle, state)
			: describeItemInState(actor, bundle, state);
	const lines: readonly NarrativeLine[] = [{ priority: 'standard', source: 'environment', text }];

	return completed(state, stampNarrativeLines(bundle, state, cursor, lines, `inspect:${action.actorId}`));
};

/**
 * `search <actor>` (D240) — going through what a BODY carries: TALLY's flank,
 * a guard's pockets (the Meridian's §6.9; the Act I specification's §13.15).
 *
 * @remarks
 * `handleSearch`'s rule, on a body. The search is recorded either way
 * (`searchedActorIds`), so a body gone through is a body gone through, and
 * re-searching never re-reveals. Its CONCEALED pockets are the find — each
 * in its own `foundNarratives` when it has them, the engine's one sentence
 * otherwise ({@link revealLinesOf}, the one builder both searches speak
 * through); what it carries in plain sight was never hidden. A search that
 * finds nothing, or nothing more, speaks the body's own `searchNarratives`,
 * else the engine's line ({@link nothingFoundLines}, the one builder both
 * searches speak through): what it still carries in plain sight, named
 * (D247), or the `searchFoundNothing` slot (`…EmptyHanded` with nothing in
 * the hands, D229's E18).
 *
 * Reach was judged at narrowing: a body heard through steel refuses the
 * search there, as it refuses every verb but speech, a gift and a look. And
 * only one level is found per act, D239's rule — a sealed bay the search
 * turns up waits on its own door, and what IT hides waits for a search of
 * the opened bay.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param action - The narrowed search
 * @param cursor - This turn's transcript cursor
 * @returns The completed search — never refused here
 */
export const handleSearchActor = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'search_actor' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const actor = requireActor(bundle, action.actorId);
	const wasAlreadySearched = state.world.searchedActorIds.includes(action.actorId);
	const searchedState = withWorld(state, { searchedActorIds: withMembership(state.world.searchedActorIds, action.actorId, true) });
	const revealed = wasAlreadySearched
		? []
		: (bundle.indexes.carriedItemIdsByActorId.get(action.actorId) ?? []).filter((carriedItemId) => requireItem(bundle, carriedItemId).containedIn?.concealed === true);
	const triggerContext = `search:${action.actorId}`;

	if (revealed.length === 0) {
		return completedSpeaking(
			bundle,
			searchedState,
			cursor,
			nothingFoundLines(bundle, state, entityRefForActor(action.actorId), actor.name, actor.searchNarratives),
			triggerContext,
		);
	}

	return completed(searchedState, stampNarrativeLines(bundle, searchedState, cursor, revealLinesOf(bundle, actor.name, revealed), triggerContext));
};

/**
 * A body's two acts (D232, D240), routed: the look, or the search.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param action - The narrowed act
 * @param cursor - This turn's transcript cursor
 * @returns The completed act
 */
export const handleBodyAct = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'inspect_actor' | 'search_actor' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult =>
	action.type === 'inspect_actor' ? handleInspectActor(bundle, state, action, cursor) : handleSearchActor(bundle, state, action, cursor);
